import { useState } from "react";
import { Alert, Badge, Card, ErrorState, LoadingState, Page, Select, Text } from "@marekl/adminer/components";
import { useSetTopbarTitle } from "@marekl/adminer/shell";
import { useCurrentCompany } from "../../lib/company";
import { useDashboard } from "../../lib/queries/dashboard";
import { formatMoney } from "../../lib/money";
import { todayCivil } from "../../lib/dates";
import { LinkComponent } from "../../lib/LinkComponent";
import { BarChart } from "../../components/BarChart";
import styles from "./DashboardPage.module.css";

const MONTHS = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];

const czk = (v: number) => formatMoney(v, "CZK");

/** "1 doklad", "3 doklady", "5 dokladů". */
const docs = (n: number) => `${n} ${n === 1 ? "doklad" : n < 5 ? "doklady" : "dokladů"}`;

/** An axis tick in whole crowns, shortened: "120 tis.", "1,2 mil." */
function tick(minor: number): string {
  const kc = minor / 100;
  const abs = Math.abs(kc);
  const nf = (n: number) => n.toLocaleString("cs-CZ", { maximumFractionDigits: 1 });
  if (abs >= 1_000_000) return `${nf(kc / 1_000_000)} mil.`;
  if (abs >= 1_000) return `${nf(kc / 1_000)} tis.`;
  return nf(kc);
}

/** "3. 11." — a civil date's day and month. */
function dayMonth(civil: string): string {
  const [, m, d] = civil.split("-").map(Number);
  return `${d}. ${m}.`;
}

/** Change against last year in whole percent, or null with nothing to compare. */
function change(now: number, before: number): number | null {
  if (before <= 0) return null;
  return Math.round(((now - before) / before) * 100);
}

/** A thin horizontal bar, `share` of the row's width, in a series colour. */
function Meter({ share, color }: { share: number; color: string }) {
  return (
    <span className={styles.meter} aria-hidden="true">
      <span style={{ width: `${Math.max(0, Math.min(1, share)) * 100}%`, background: color }} />
    </span>
  );
}

/**
 * "Přehled" (docs/R1_PLAN.md batch 1b): revenue per month against the year
 * before, the year to date and the top customers — all from issued
 * documents' stored totals, in CZK. The API's VAT periods are not shown
 * (owner, 2026-11-03).
 */
export function DashboardPage() {
  const { id: companyId } = useCurrentCompany();
  useSetTopbarTitle("Přehled");
  const thisYear = Number(todayCivil().slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const q = useDashboard(companyId, year);

  if (q.isLoading) return <LoadingState />;
  if (q.error && !q.data) return <ErrorState title="Přehled se nepodařilo načíst" error={q.error} onRetry={q.refetch} />;
  const d = q.data!;
  const yearTotal = d.months.reduce((s, m) => s + m.revenue, 0);
  const ytd = d.year_to_date;
  const pct = change(ytd.revenue, ytd.previous);
  const ytdMax = Math.max(ytd.revenue, ytd.previous, 1);
  const years = Array.from({ length: 6 }, (_, i) => thisYear - i);
  const current = d.year === thisYear;

  return (
    <Page>
      <Page.Content>
        <div className={styles.stack}>
          <Card>
            <Card.Header
              title="Tržby"
              subtitle="Bez DPH, podle data vystavení."
              actions={
                <Select aria-label="Rok" value={String(year)} onChange={(e) => setYear(Number(e.target.value))}>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </Select>
              }
            />
            <Card.Content>
              <BarChart
                title={`Tržby po měsících ${d.year} a ${d.year - 1}`}
                categories={MONTHS.map((m, i) => ({ short: String(i + 1), long: `${m} ${d.year}` }))}
                series={[
                  { label: String(d.year), values: d.months.map((m) => m.revenue), color: "var(--faktio-chart-current)" },
                  { label: String(d.year - 1), values: d.months.map((m) => m.previous), color: "var(--faktio-chart-previous)" },
                ]}
                format={czk}
                formatTick={tick}
                minSpan={1_000_000}
              />
              {d.missing_rate > 0 && (
                <Alert variant="warn" className={styles.note}>
                  Bez kurzu ČNB nezapočteno: {docs(d.missing_rate)} v cizí měně ({d.year - 1}–{d.year}).
                </Alert>
              )}
            </Card.Content>
          </Card>
          <div className={styles.cards}>
            <Card>
              <Card.Header
                title={current ? "Letos" : `Rok ${d.year}`}
                subtitle={current ? `k ${dayMonth(ytd.through)}, proti stejnému dni loni` : `proti roku ${d.year - 1}`}
                actions={
                  pct !== null && (
                    <Badge
                      variant={pct > 0 ? "ok" : pct < 0 ? "err" : "muted"}
                      role="img"
                      aria-label={pct === 0 ? "stejně jako loni" : `${pct > 0 ? "více" : "méně"} než loni o ${Math.abs(pct)} %`}
                    >
                      {pct > 0 ? "▲ " : pct < 0 ? "▼ " : ""}{Math.abs(pct)} %
                    </Badge>
                  )
                }
              />
              <Card.Content>
                <dl className={styles.compare}>
                  <div>
                    <dt>{d.year}</dt>
                    <dd><Meter share={ytd.revenue / ytdMax} color="var(--faktio-chart-current)" /></dd>
                    <dd className={styles.hero} data-testid="ytd">{czk(ytd.revenue)}</dd>
                  </div>
                  <div>
                    <dt>{d.year - 1}</dt>
                    <dd><Meter share={ytd.previous / ytdMax} color="var(--faktio-chart-previous)" /></dd>
                    <dd className={styles.amount}>{czk(ytd.previous)}</dd>
                  </div>
                </dl>
              </Card.Content>
            </Card>

            <Card>
              <Card.Header title="Největší odběratelé" subtitle={current ? undefined : String(d.year)} />
              <Card.Content>
                {d.top_customers.length === 0 ? (
                  <Text variant="muted">Zatím žádné vydané doklady.</Text>
                ) : (
                  <ol className={styles.customers}>
                    {d.top_customers.map((c) => (
                      <li key={c.contact_id ?? c.name}>
                        <span className={styles.customerName}>
                          {c.contact_id ? <LinkComponent to={`/c/${companyId}/contacts/${c.contact_id}`}>{c.name}</LinkComponent> : c.name}
                        </span>
                        <span className={styles.amount}>{czk(c.revenue)}</span>
                        <Text variant="muted" className={styles.share}>
                          {/* A share only of a positive whole: credit notes can push either below zero. */}
                          {yearTotal > 0 && c.revenue > 0 ? `${Math.min(100, Math.round((c.revenue / yearTotal) * 100))} %` : ""}
                        </Text>
                      </li>
                    ))}
                  </ol>
                )}
              </Card.Content>
            </Card>

          </div>
        </div>
      </Page.Content>
    </Page>
  );
}
